// src/pages/admin/ReportsPage.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ZONAS = [
    "Saúde", "Morumbi", "Aclimação", "Ipiranga", "Mooca", "Carrão",
    "Itaquera", "Guarulhos", "Santo André", "São Bernardo do Campo",
    "São Caetano do Sul", "Litoral"
];

const REPORT_LABELS = {
    status:           'Relatório por Status',
    zona:             'Relatório por Região de Atuação',
    leitura_mensagem: 'Relatório de Leitura de Mensagem',
    por_gerente:      'Relatório de Parceiros por Gerente',
};

// Salmão corporativo
const COR  = '#FA8072';
const RED  = { r: 250, g: 128, b: 114 }; // #FA8072

// 'managerId' é opcional. Se passado, filtra os relatórios apenas para aquele gerente.
function ReportsPage({ managerId = null }) {
    const [reportType,    setReportType]    = useState('');
    const [filterValue,   setFilterValue]   = useState('');
    const [filterStatus,  setFilterStatus]  = useState('');   // '' = Todos
    const [results,       setResults]       = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [messagesList,  setMessagesList]  = useState([]);
    const [gerentes,      setGerentes]      = useState([]);
    const [reportHeader,  setReportHeader]  = useState('');
    const [generatingPdf,  setGeneratingPdf]  = useState(false);
    const [generatingXlsx, setGeneratingXlsx] = useState(false);

    // Carrega gerentes quando tipo = por_gerente
    useEffect(() => {
        if (reportType === 'por_gerente') {
            getDocs(query(collection(db, "usuarios"), where("tipoUsuario", "==", "gerente")))
                .then(snap => {
                    const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
                    setGerentes(lista);
                });
        }
    }, [reportType]);

    // Carrega mensagens quando tipo = leitura_mensagem
    useEffect(() => {
        if (reportType === 'leitura_mensagem') {
            let q = query(collection(db, "mensagens"), orderBy("dataEnvio", "desc"));
            if (managerId) q = query(collection(db, "mensagens"), where("remetenteId", "==", managerId), orderBy("dataEnvio", "desc"));
            getDocs(q).then(snap => setMessagesList(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        }
    }, [reportType, managerId]);

    const handleGenerateReport = async () => {
        setLoading(true);
        setResults([]);
        setReportHeader('');
        try {
            const usuariosRef = collection(db, "usuarios");
            const baseConstraints = managerId ? [where("gerenteId", "==", managerId)] : [];
            let docs = [];

            if (reportType === 'status') {
                // Para "por status", o filterValue (ativo/inativo) é o critério principal;
                // o filterStatus universal acumula se também preenchido
                const constraints = [...baseConstraints, where("status", "==", filterValue)];
                const snap = await getDocs(query(usuariosRef, ...constraints));
                docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setReportHeader(`Parceiros com status: ${filterValue}`);

            } else if (reportType === 'zona') {
                const constraints = [...baseConstraints, where("zonaAtuacao", "array-contains", filterValue)];
                const snap = await getDocs(query(usuariosRef, ...constraints));
                docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setReportHeader(`Zona de atuação: ${filterValue}`);

            } else if (reportType === 'leitura_mensagem') {
                const msg = messagesList.find(m => m.id === filterValue);
                const lidoPorIds = msg?.lidoPor || [];
                if (lidoPorIds.length > 0) {
                    const snap = await getDocs(query(usuariosRef, ...baseConstraints));
                    docs = snap.docs.map(d => ({ ...d.data(), id: d.id })).filter(u => lidoPorIds.includes(u.id));
                }
                setReportHeader('Leitores da mensagem');

            } else if (reportType === 'por_gerente') {
                const gerente = gerentes.find(g => g.id === filterValue);
                const snap = await getDocs(query(usuariosRef, where("gerenteId", "==", filterValue)));
                docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setReportHeader(`Parceiros do gerente: ${gerente?.nome || filterValue}`);
            }

            // Filtro de status universal (aplica a todos os tipos)
            if (filterStatus) {
                docs = docs.filter(d => d.status === filterStatus);
            }

            docs.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
            setResults(docs);

        } catch (err) {
            console.error("Erro ao gerar relatório:", err);
            alert("Erro ao gerar. Verifique o console.");
        } finally {
            setLoading(false);
        }
    };

    const tipoLabel = t => ({ autonomo: 'Autônomo', imobiliaria: 'Imobiliária', gerente: 'Gerente', administrador: 'Administrador' }[t] || t);

    const handleExportPdf = async () => {
        if (!results.length) return;
        setGeneratingPdf(true);
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const now   = new Date();
            const dataHora       = now.toLocaleDateString('pt-BR') + ' às ' + now.toLocaleTimeString('pt-BR');
            const tituloRelatorio = REPORT_LABELS[reportType] || 'Relatório';
            const { r, g, b }    = RED;

            // ── Logo ──────────────────────────────────────────────────────
            let logoDataUrl = null;
            try {
                const resp = await fetch('/logo.png');
                const blob = await resp.blob();
                logoDataUrl = await new Promise(res => {
                    const reader = new FileReader();
                    reader.onloadend = () => res(reader.result);
                    reader.readAsDataURL(blob);
                });
            } catch (_) {}

            // ── Cabeçalho vermelho clarinho ───────────────────────────────
            const headerH = 28;
            doc.setFillColor(r, g, b);
            doc.rect(0, 0, pageW, headerH, 'F');

            if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', 8, 5, 0, 18);

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(255, 255, 255);
            doc.text(tituloRelatorio, pageW / 2, 12, { align: 'center' });

            // Subtítulo + filtro de status
            let subtitulo = reportHeader;
            if (filterStatus) subtitulo += ` · ${filterStatus === 'ativo' ? 'Somente Ativos' : 'Somente Inativos'}`;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(255, 220, 220);
            doc.text(subtitulo, pageW / 2, 21, { align: 'center' });

            doc.setFontSize(8);
            doc.setTextColor(255, 220, 220);
            doc.text(`Gerado em: ${dataHora}`, pageW - 8, 12, { align: 'right' });
            doc.text(`Total: ${results.length} parceiro(s)`, pageW - 8, 21, { align: 'right' });

            // ── Colunas ───────────────────────────────────────────────────
            const columns = [
                { header: 'Nome',     dataKey: 'nome' },
                { header: 'E-mail',   dataKey: 'email' },
                { header: 'Tipo',     dataKey: 'tipo' },
                { header: 'Telefone', dataKey: 'telefone' },
            ];
            if (reportType === 'por_gerente') {
                columns.push({ header: 'Zona de Atuação', dataKey: 'zona' });
            }

            const rows = results.map(row => ({
                nome:     row.nome     || '-',
                email:    row.email    || '-',
                tipo:     tipoLabel(row.tipoUsuario),
                telefone: row.telefone || '-',
                zona:     Array.isArray(row.zonaAtuacao) ? row.zonaAtuacao.join(', ') : (row.zonaAtuacao || '-'),
            }));

            // ── Tabela ────────────────────────────────────────────────────
            autoTable(doc, {
                startY: headerH + 4,
                columns,
                body: rows,
                styles:       { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
                headStyles:   { fillColor: [r, g, b], textColor: 255, fontStyle: 'bold', halign: 'center' },
                alternateRowStyles: { fillColor: [255, 245, 245] },
                columnStyles: {
                    nome:     { cellWidth: 60 },
                    email:    { cellWidth: 68 },
                    tipo:     { cellWidth: 30, halign: 'center' },
                    telefone: { cellWidth: 35, halign: 'center' },
                    zona:     { cellWidth: 65 },
                },
                didDrawPage: () => {
                    doc.setFontSize(8);
                    doc.setTextColor(150);
                    doc.setFont('helvetica', 'normal');
                    const pageNum   = doc.internal.getCurrentPageInfo().pageNumber;
                    const totalPgs  = doc.internal.getNumberOfPages();
                    doc.text(
                        `Página ${pageNum} de ${totalPgs}  |  MBigucci — Gestão de Parceiros`,
                        pageW / 2, pageH - 5, { align: 'center' }
                    );
                },
            });

            const slug = tituloRelatorio
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            doc.save(`${slug}_${now.toISOString().slice(0, 10)}.pdf`);

        } catch (err) {
            console.error('Erro ao gerar PDF:', err);
            alert('Não foi possível gerar o PDF. Verifique sua conexão e tente novamente.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleExportXlsx = () => {
        if (!results.length) return;
        setGeneratingXlsx(true);
        try {
            const tituloRelatorio = REPORT_LABELS[reportType] || 'Relatorio';
            const now = new Date();

            // Monta os dados da planilha
            const sheetData = results.map(row => ({
                'Nome':           row.nome     || '-',
                'E-mail':         row.email    || '-',
                'Tipo':           tipoLabel(row.tipoUsuario),
                'Telefone':       row.telefone || '-',
                'Status':         row.status   || '-',
                'Zona de Atuação': Array.isArray(row.zonaAtuacao)
                    ? row.zonaAtuacao.join(', ')
                    : (row.zonaAtuacao || '-'),
                'CRECI':          row.creci    || '-',
                'CRECI Validado': row.creciValidado ? 'Sim' : 'Não',
                'CRECI Validado Em': row.creciValidadoEm
                    ? new Date(row.creciValidadoEm).toLocaleDateString('pt-BR')
                    : '-',
            }));

            const ws = XLSX.utils.json_to_sheet(sheetData);

            // Ajusta largura das colunas automaticamente
            const colWidths = Object.keys(sheetData[0] || {}).map(key => ({
                wch: Math.max(key.length, ...sheetData.map(r => String(r[key] || '').length)) + 2
            }));
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Relatório');

            const slug = tituloRelatorio
                .toLowerCase()
                .normalize('NFD').replace(/[̀-ͯ]/g, '')
                .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            XLSX.writeFile(wb, `${slug}_${now.toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            console.error('Erro ao gerar Excel:', err);
            alert('Não foi possível gerar o Excel. Tente novamente.');
        } finally {
            setGeneratingXlsx(false);
        }
    };

    // Botão "Gerar" habilitado somente quando o filtro específico estiver preenchido
    const canGenerate = !!filterValue || reportType === '';

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Gerador de Relatórios</Typography>
                <Grid container spacing={2} alignItems="center">

                    {/* Col 1 — Tipo */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth sx={{ minWidth: 180 }}>
                            <InputLabel>Tipo de Relatório</InputLabel>
                            <Select
                                value={reportType}
                                label="Tipo de Relatório"
                                onChange={e => { setReportType(e.target.value); setFilterValue(''); setResults([]); }}
                            >
                                <MenuItem value="status">Por Status (Ativo/Inativo)</MenuItem>
                                <MenuItem value="zona">Por Região de Atuação</MenuItem>
                                <MenuItem value="leitura_mensagem">Quem leu a Mensagem X</MenuItem>
                                {!managerId && <MenuItem value="por_gerente">Parceiros por Gerente</MenuItem>}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Col 2 — Filtro específico do tipo */}
                    <Grid item xs={12} sm={6} md={3}>
                        {reportType === 'status' && (
                            <FormControl fullWidth sx={{ minWidth: 180 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={filterValue} label="Status" onChange={e => setFilterValue(e.target.value)}>
                                    <MenuItem value="ativo">Ativo</MenuItem>
                                    <MenuItem value="inativo">Inativo</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {reportType === 'zona' && (
                            <FormControl fullWidth sx={{ minWidth: 180 }}>
                                <InputLabel>Região</InputLabel>
                                <Select value={filterValue} label="Região" onChange={e => setFilterValue(e.target.value)}>
                                    {ZONAS.map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        {reportType === 'leitura_mensagem' && (
                            <FormControl fullWidth sx={{ minWidth: 180 }}>
                                <InputLabel>Selecione a Mensagem</InputLabel>
                                <Select value={filterValue} label="Selecione a Mensagem" onChange={e => setFilterValue(e.target.value)}>
                                    {messagesList.map(m => (
                                        <MenuItem key={m.id} value={m.id}>
                                            {m.dataEnvio ? new Date(m.dataEnvio.seconds * 1000).toLocaleDateString() : ''} — {m.mensagemTexto?.substring(0, 30)}...
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {reportType === 'por_gerente' && (
                            <FormControl fullWidth sx={{ minWidth: 180 }}>
                                <InputLabel>Selecione o Gerente</InputLabel>
                                <Select value={filterValue} label="Selecione o Gerente" onChange={e => setFilterValue(e.target.value)}>
                                    {gerentes.length === 0
                                        ? <MenuItem disabled><em>Carregando...</em></MenuItem>
                                        : gerentes.map(g => <MenuItem key={g.id} value={g.id}>{g.nome}</MenuItem>)
                                    }
                                </Select>
                            </FormControl>
                        )}
                    </Grid>

                    {/* Col 3 — Filtro de status universal */}
                    <Grid item xs={12} sm={6} md={3}>
                        {reportType && (
                            <FormControl fullWidth sx={{ minWidth: 180 }}>
                                <InputLabel>Situação do Cadastro</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Situação do Cadastro"
                                    onChange={e => setFilterStatus(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="ativo">Somente Ativos</MenuItem>
                                    <MenuItem value="inativo">Somente Inativos</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Grid>

                    {/* Col 4 — Botão */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleGenerateReport}
                            disabled={!filterValue || loading}
                            sx={{ bgcolor: COR, '&:hover': { bgcolor: '#e8614f' }, height: 56 }}
                        >
                            Gerar Relatório
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress sx={{ color: '#FA8072' }} />
                </Box>
            )}

            {!loading && results.length > 0 && (
                <TableContainer component={Paper}>
                    <Box sx={{
                        p: 2,
                        bgcolor: '#fff5f5',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 1,
                        borderBottom: '1px solid #ffcdd2'
                    }}>
                        <Typography variant="subtitle1">
                            <strong>{reportHeader}</strong>
                            {filterStatus && <> · <em>{filterStatus === 'ativo' ? 'Somente Ativos' : 'Somente Inativos'}</em></>}
                            {' '}— {results.length} parceiro{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={generatingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                                onClick={handleExportPdf}
                                disabled={generatingPdf || generatingXlsx}
                                sx={{ color: '#FA8072', borderColor: '#FA8072', '&:hover': { borderColor: '#e8614f', color: '#e8614f' } }}
                            >
                                {generatingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={generatingXlsx ? <CircularProgress size={16} /> : <TableChartIcon />}
                                onClick={handleExportXlsx}
                                disabled={generatingPdf || generatingXlsx}
                                sx={{ color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', color: '#1b5e20' } }}
                            >
                                {generatingXlsx ? 'Gerando Excel...' : 'Exportar Excel'}
                            </Button>
                        </Box>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#FA8072' }}>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Nome</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>E-mail</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Tipo</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Telefone</TableCell>
                                {reportType === 'por_gerente' && <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Zona de Atuação</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{ bgcolor: index % 2 === 0 ? '#fff' : '#fff5f5', '&:hover': { bgcolor: '#ffebee' } }}
                                >
                                    <TableCell>{row.nome}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>{tipoLabel(row.tipoUsuario)}</TableCell>
                                    <TableCell>{row.telefone || '-'}</TableCell>
                                    {reportType === 'por_gerente' && (
                                        <TableCell>
                                            {Array.isArray(row.zonaAtuacao) ? row.zonaAtuacao.join(', ') : (row.zonaAtuacao || '-')}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && results.length === 0 && filterValue && (
                <Alert severity="info">Nenhum resultado encontrado para os filtros selecionados.</Alert>
            )}
        </Box>
    );
}

export default ReportsPage;
